import { memo, useCallback, useEffect } from "react";
import type { FullScreenHandle } from "react-full-screen";
import Control from "react-leaflet-custom-control";
import { useDispatch } from "react-redux";

import { ControlPositionType } from "@cobliteam/common-types";

import type { TrackingDevice } from "@cobliteam/api-lib";

import { Analytics } from "@cobliteam/analytics";

import { changeMapRef } from "@cobliteam/store";

import { useMap } from "@cobliteam/hooks";

import { MapLegend } from "../../MapLegend/MapLegend";
import { FullscreenControl } from "../FullscreenControl/FullscreenControl";
import MapControlDropdown from "../MapControlDropdown/MapControlDropdown";
import { type MapType, MapTypeControl } from "../MapTypeControl/MapTypeControl";
import ZoomControl from "../ZoomControl/ZoomControl";

export interface TrackingMapControlsProps {
  fullscreenHandler: FullScreenHandle;
  showGeofences: boolean;
  showMediaEvents: Boolean | undefined;
  showRoadSpeedEvents: boolean;
  showVehicleSpeedEvents: boolean;
  showGeofencesEvents: boolean;
  selectedDevice: TrackingDevice | undefined;
  mapType: MapType;
  useMapMatch: boolean;
  setMapType: () => {};
  setShowRoadSpeedEvents: () => {};
  setShowVehicleSpeedEvents: () => {};
  setShowGeofencesEvents: () => {};
  handleToggleGeofences: () => {};
  setMediaEvents: () => {};
  setUseMapMatch: () => {};
}

const TrackingMapControls = ({
  fullscreenHandler,
  showGeofences,
  showMediaEvents,
  showRoadSpeedEvents,
  showVehicleSpeedEvents,
  showGeofencesEvents,
  selectedDevice,
  mapType,
  useMapMatch,
  setMapType,
  setShowRoadSpeedEvents,
  setShowVehicleSpeedEvents,
  setShowGeofencesEvents,
  handleToggleGeofences,
  setMediaEvents,
  setUseMapMatch,
}: TrackingMapControlsProps) => {
  const { map } = useMap();
  const dispatch = useDispatch();

  const handleFullscreen = (active: boolean) => {
    Analytics.track("Tracking Fullscreen Toggled", { active });
    return active ? fullscreenHandler.enter() : fullscreenHandler.exit();
  };

  const createMapRef = useCallback(() => {
    if (map) {
      dispatch(changeMapRef(map));

      // remove tabindex of map and fix select option in search box
      const documentElement = document.getElementById("leaflet-map-tracking");
      if (documentElement) {
        documentElement.removeAttribute("tabindex");
      }
    }
  }, [map]);

  useEffect(() => {
    createMapRef();
    return () => {
      dispatch(changeMapRef(null));
    };
  }, [createMapRef, dispatch]);

  return (
    <>
      <Control position={ControlPositionType.TOP_LEFT}>
        <MapLegend showMediaEvents={Boolean(showMediaEvents)} />
        <ZoomControl />
        <FullscreenControl
          active={fullscreenHandler.active}
          onToggle={handleFullscreen}
        />
      </Control>

      <Control position={ControlPositionType.TOP_RIGHT}>
        <MapControlDropdown
          showGeofences={showGeofences}
          showRoadSpeedEvents={showRoadSpeedEvents}
          showVehicleSpeedEvents={showVehicleSpeedEvents}
          showMediaEvents={Boolean(showMediaEvents)}
          showGeofencesEvents={showGeofencesEvents}
          setShowRoadSpeedEvents={setShowRoadSpeedEvents}
          setShowVehicleSpeedEvents={setShowVehicleSpeedEvents}
          setShowGeofencesEvents={setShowGeofencesEvents}
          handleToggleGeofences={handleToggleGeofences}
          setMediaEvents={setMediaEvents}
          selectedDevice={selectedDevice}
          useMapMatch={useMapMatch}
          setUseMapMatch={setUseMapMatch}
        />
      </Control>

      <Control position={ControlPositionType.BOTTOM_LEFT}>
        <MapTypeControl currentType={mapType} onTypeChange={setMapType} />
      </Control>
    </>
  );
};

export default memo(TrackingMapControls);
